import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlineUsers, HiOutlineSparkles, HiOutlineInbox } from 'react-icons/hi';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-800' },
  { value: 'acknowledged', label: 'Acknowledged', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-800' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-600' },
];

export default function PMDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingRequest, setUpdatingRequest] = useState(null);
  const { addToast } = useToast();

  const loadData = () => {
    api.get('/pm/dashboard').then(({ data }) => {
      setStats(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleStatusChange = async (requestId, newStatus) => {
    if (newStatus === 'pending') return; // Can't set back to pending
    setUpdatingRequest(requestId);
    try {
      await api.put(`/pm/requests/${requestId}`, { status: newStatus });
      addToast(`Request updated to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      loadData();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to update', 'error');
    }
    setUpdatingRequest(null);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Partner Manager Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Link to="/pm/partners" className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-2">
            <HiOutlineUsers className="w-6 h-6 text-v2-blue" />
            <span className="text-sm font-medium text-gray-500">Assigned Partners</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.partnerCount || 0}</p>
        </Link>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <HiOutlineSparkles className="w-6 h-6 text-purple-500" />
            <span className="text-sm font-medium text-gray-500">Total Assets Created</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.totalAssets || 0}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <HiOutlineInbox className="w-6 h-6 text-amber-500" />
            <span className="text-sm font-medium text-gray-500">Pending Requests</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.pendingRequests || 0}</p>
        </div>
      </div>

      {/* Recent requests */}
      {stats?.recentRequests?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Marketing Strategy Requests</h2>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Partner</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats.recentRequests.map(r => {
                const currentStatus = STATUS_OPTIONS.find(s => s.value === r.status) || STATUS_OPTIONS[0];
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{r.partner?.companyName || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {r.type === 'strategy_session' ? 'Strategy Session' : r.type === 'access_interest' ? 'Access Request' : 'Call Request'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={r.status}
                        onChange={e => handleStatusChange(r.id, e.target.value)}
                        disabled={updatingRequest === r.id}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${currentStatus.color} ${updatingRequest === r.id ? 'opacity-50' : ''}`}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s.value} value={s.value} disabled={s.value === 'pending'}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
